import React from "react";
import Canvas from "./Canvas";
import ArrowLayer, { Arrow } from "./ArrowLayer";
import CanvasNodeCard from "./CanvasNodeCard";
import LoadingSpinner from "../ui/LoadingSpinner";

export interface NodePosition {
  id: number;
  x: number;
  y: number;
}

interface TransitionLike {
  id: string | number;
  current_node_id: number;
  next_node_id: number;
}

interface NodeGraphProps {
  nodes: any[];
  positions: NodePosition[];
  onPositionsChange: (positions: NodePosition[]) => void;
  onPositionsDirty?: (dirty: boolean) => void;
  zoom: number;
  panX: number;
  panY: number;
  onCanvasMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  transitions?: TransitionLike[];
  onEditNode?: (node: any) => void;
  loading?: boolean;
  className?: string;
  highlightGlobal?: boolean;
}

export default function NodeGraph({
  nodes,
  positions,
  onPositionsChange,
  onPositionsDirty,
  zoom,
  panX,
  panY,
  onCanvasMouseDown,
  transitions = [],
  onEditNode,
  loading = false,
  className,
  highlightGlobal = false,
}: NodeGraphProps) {
  const [draggingId, setDraggingId] = React.useState<number | null>(null);
  const lastMouseRef = React.useRef<{ x: number; y: number } | null>(null);

  const getPos = React.useCallback(
    (nid: number) => {
      return positions.find((p) => p.id === nid) || { id: nid, x: 0, y: 0 };
    },
    [positions],
  );

  const arrows: Arrow[] = transitions.map((tr) => {
    const a = getPos(tr.current_node_id);
    const b = getPos(tr.next_node_id);
    return {
      id: tr.id,
      x1: a.x + 112,
      y1: a.y + 32,
      x2: b.x + 112,
      y2: b.y + 16,
    };
  });

  const onCardMouseDown = (e: React.MouseEvent, nodeId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingId(nodeId);
    lastMouseRef.current = { x: e.clientX, y: e.clientY };
    document.body.style.userSelect = "none";
  };

  React.useEffect(() => {
    if (draggingId == null) return;
    const onMouseMove = (e: MouseEvent) => {
      if (lastMouseRef.current == null) return;
      const dx = (e.clientX - lastMouseRef.current.x) / zoom;
      const dy = (e.clientY - lastMouseRef.current.y) / zoom;
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
      onPositionsChange(
        positions.map((p) => {
          if (p.id !== draggingId) return p;
          const nx = Math.max(0, p.x + dx);
          const ny = Math.max(0, p.y + dy);
          return { ...p, x: nx, y: ny };
        }),
      );
      onPositionsDirty && onPositionsDirty(true);
    };
    const onMouseUp = () => {
      setDraggingId(null);
      lastMouseRef.current = null;
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [draggingId, zoom, positions, onPositionsChange, onPositionsDirty]);

  return (
    <div
      className={[
        "relative w-full h-[70vh] bg-gray-50 border rounded overflow-hidden",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseDown={onCanvasMouseDown}
    >
      <ArrowLayer zoom={zoom} panX={panX} panY={panY} arrows={arrows} />
      <Canvas zoom={zoom} panX={panX} panY={panY} className="absolute inset-0">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <LoadingSpinner />
          </div>
        ) : (
          nodes.map((node) => {
            const pos = positions.find((p) => p.id === node.id) || {
              id: node.id,
              x: 100,
              y: 100,
            };
            const msg =
              (node.messages &&
                (node.messages["en"] || Object.values(node.messages)[0])) ||
              "";
            const title = msg || node.node_name;
            const subtitle = msg ? node.node_name : undefined;
            const isGlobal =
              (typeof node?.node_group_id === "number" &&
                Number.isFinite(node?.node_group_id) &&
                node?.node_group_id > 0) ||
              !(
                typeof node?.script_id === "number" &&
                Number.isFinite(node?.script_id) &&
                node?.script_id > 0
              );
            const isSpecialName =
              typeof node?.node_name === "string" &&
              ["start", "end_conversation"].includes(
                node.node_name.toLowerCase(),
              );
            return (
              <div
                key={node.id}
                className="absolute"
                style={{
                  left: pos.x,
                  top: pos.y,
                  cursor: draggingId === node.id ? "grabbing" : "grab",
                }}
                onMouseDown={(e) => onCardMouseDown(e, node.id)}
                data-node-card="1"
              >
                <CanvasNodeCard
                  title={title}
                  subtitle={subtitle}
                  onEdit={
                    onEditNode && !isSpecialName
                      ? () => onEditNode(node)
                      : undefined
                  }
                  isGlobal={isGlobal}
                  highlightGlobal={highlightGlobal}
                />
              </div>
            );
          })
        )}
      </Canvas>
    </div>
  );
}
